import axios from 'axios';
import FileUtils from '../utils/file.utils';

const adminUrl = process.env.VUE_APP_ADMIN_URL;

// Los `File` nunca viajan tal cual: un archivo nuevo se envía en base64 (sin prefijo data:)
// y tiene precedencia sobre la URL; sin archivo se conserva la URL (o null para quitarla).
export async function buildCompanyPayload({ logoFile, iconAppFile, ...company }) {
  const payload = { ...company };
  // El nombre del archivo viaja para que el backend lo conserve en la URL.
  if (logoFile) {
    payload.logoBase64 = await FileUtils.getBase64(logoFile);
    payload.logoFileName = logoFile.name;
  }
  if (iconAppFile) {
    payload.iconAppBase64 = await FileUtils.getBase64(iconAppFile);
    payload.iconAppFileName = iconAppFile.name;
  }
  return payload;
}

function toSaveResult(response) {
  if (response.data.status === 'ok') return { status: 'ok', data: response.data.data };
  return { status: 'error', code: response.data.code, message: response.data.message };
}

// code / message / httpCode son aditivos: los callers que solo miran `status` no cambian.
function toSaveError(err) {
  const body = err.response?.data || {};
  return {
    status: 'error',
    code: body.error?.code ?? body.code,
    message: body.error?.message ?? body.message,
    httpCode: err.response?.status
  };
}

export default class CompaniesService {
  static async getCompanies(page, limit, search, activeFilter = 'active', countryId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      let url = `page=${page}&limit=${limit}&active=${activeFilter}`;
      if (search) {
        url += `&search=${search}`;
      }
      if (countryId) {
        url += `&countryId=${countryId}`;
      }
      const response = await axios.get(`${adminUrl}/company/list?${url}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  static async getCompanyById(companyId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(`${adminUrl}/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.company };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  // company: campos de la empresa + logoFile / iconAppFile (File o null). Se envían como
  // logoBase64 / iconAppBase64 solo cuando hay archivo nuevo; logoUrl / urlIconApp viajan tal cual.
  static async createCompany(company) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const payload = await buildCompanyPayload(company);
      const response = await axios.post(`${adminUrl}/companies`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return toSaveResult(response);
    } catch (err) {
      return toSaveError(err);
    }
  }

  static async updateCompany(companyId, company) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const payload = await buildCompanyPayload(company);
      const response = await axios.put(`${adminUrl}/companies/${companyId}`, payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      return toSaveResult(response);
    } catch (err) {
      return toSaveError(err);
    }
  }

  static async getCompanyPlans(page, limit, companyId, visible) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const url = `${adminUrl}/company/plans?page=${page}&limit=${limit}&id=${companyId}&visible=${visible}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        console.log(response.data.data);
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'err' };
    }
  }

  static async getAllCompanyPlans(page, limit, companyId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const url = `${adminUrl}/company?page=${page}&limit=${limit}&companyId=${companyId}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'err' };
    }
  }

  static async getCompaniesWithCountry() {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const response = await axios.get(`${adminUrl}/company/all-list`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (error) {
      return { status: 'error' };
    }
  }

  // ---- User company maintainer (Vision360 detail) ----

  static async getUserCompanyAccesses(userId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const url = `${adminUrl}/company-users/users/${userId}/accesses`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'error' };
    }
  }

  static async associateUserCompany(userId, payload) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const url = `${adminUrl}/company-users/users/${userId}/accesses`;
      const response = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      // Los errores con código (p. ej. PLAN_NOT_IN_CONTRATO) lo traen aparte del mensaje.
      return { status: 'error', message: err.response?.data?.code || err.response?.data?.message };
    }
  }

  static async updateAccessAccounts(ucaId, totalAccounts, confirm = false) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const url = `${adminUrl}/company-users/accesses/${ucaId}/accounts`;
      const response = await axios.patch(
        url,
        { totalAccounts, confirm },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'error', message: err.response?.data?.message };
    }
  }

  static async setAccessStatus(ucaId, visible) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const url = `${adminUrl}/company-users/accesses/${ucaId}/status`;
      const response = await axios.patch(
        url,
        { visible },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'error', message: err.response?.data?.message };
    }
  }

  // planId null = el acceso permite todos los planes del contrato.
  static async changeAccessPlan(ucaId, planId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const url = `${adminUrl}/company-users/accesses/${ucaId}/plan`;
      const response = await axios.patch(
        url,
        { planId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'error', message: err.response?.data?.code || err.response?.data?.message };
    }
  }

  static async deleteAccess(ucaId) {
    try {
      const accessToken = JSON.parse(localStorage.getItem('accessToken'));
      const url = `${adminUrl}/company-users/accesses/${ucaId}`;
      const response = await axios.delete(url, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (response.data.status === 'ok') {
        return { status: 'ok', data: response.data.data };
      } else {
        return { status: 'error' };
      }
    } catch (err) {
      return { status: 'error', message: err.response?.data?.message };
    }
  }

}
